import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack, Group, Title, Text, Paper, Badge, Code, ScrollArea, Loader, Anchor,
  Accordion, Tabs, Table, SimpleGrid, Button, Menu,
} from '@mantine/core';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import useAuthenticatedPost from '../../../hooks/useAuthenticatedPost';
import config from '../../../config';

// Column names are semantic (not ordinal), so the step number is display-only and a reorder is a one-line
// change here. outCol/inCol name the run's telemetry columns for the LLM steps.
const STAGES = [
  { n: 1, key: 'context_status', err: 'context_error', name: 'Context assembly', kind: 'deterministic' },
  { n: 2, key: 'score_status', err: 'score_error', name: 'Numeric scoring', kind: 'deterministic' },
  { n: 3, key: 'pricing_status', err: 'pricing_error', name: 'Pricing floor', kind: 'deterministic' },
  { n: 4, key: 'summarize_status', err: 'summarize_error', name: 'LLM summarize', kind: 'llm',
    prefix: 'summarize', outCol: 'summarize_output_json', inCol: 'summarize_input_json' },
  { n: 5, key: 'judgement_status', err: 'judgement_error', name: 'LLM judgement (per line)', kind: 'llm',
    prefix: 'judgement', outCol: 'judgement_output_json', inCol: 'judgement_input_json' },
];

const COVERAGE_BADGE = { have_all: 'green', have_some: 'yellow', have_none: 'red', unknown: 'gray' };
const STANCE_COLOR = { prioritize: 'green', standard: 'blue', cautious: 'orange', decline: 'red' };

function stColor(s) {
  return s === 'success' ? 'green' : s === 'partial' ? 'yellow' : s === 'failed' ? 'red' : 'gray';
}
function fmt(iso) { return iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' }) : '—'; }

// Pretty-print JSON (string or object); fall back to raw text.
function pretty(v) {
  if (v === null || v === undefined || v === '') return '(none)';
  try { return JSON.stringify(typeof v === 'string' ? JSON.parse(v) : v, null, 2); }
  catch { return String(v); }
}
function JsonBlock({ value, maxHeight = 380 }) {
  return (
    <ScrollArea.Autosize mah={maxHeight}>
      <Code block style={{ fontSize: 12 }}>{pretty(value)}</Code>
    </ScrollArea.Autosize>
  );
}

export default function DealPipeline() {
  const { publicId } = useParams();
  const navigate = useNavigate();

  const url = useMemo(
    () => `${config.apiBaseUrl}/api/vesper/deals/${encodeURIComponent(publicId)}/pipeline`,
    [publicId],
  );
  const { data, isLoading, error, refetch } = useAuthenticatedData(url);
  const { post, isLoading: isEnriching } = useAuthenticatedPost();
  const [enrichMsg, setEnrichMsg] = useState(null);

  const runEnrich = async (adjudicate) => {
    setEnrichMsg(null);
    try {
      const q = adjudicate ? '?adjudicate=true' : '';
      const res = await post(`${config.apiBaseUrl}/api/vesper/deals/${encodeURIComponent(publicId)}/enrich${q}`, null);
      setEnrichMsg({ ok: true, text: `Enriched: ${res?.status ?? 'ok'}${res?.priorityScore != null ? `, score ${res.priorityScore}` : ''}` });
      refetch();
    } catch (e) {
      setEnrichMsg({ ok: false, text: `Enrich failed: ${e?.response?.data?.message || e.message}` });
    }
  };

  if (isLoading) return <Stack align="center" mt="xl"><Loader /><Text c="dimmed">Loading pipeline…</Text></Stack>;
  if (error) return <Text c="red" p="md">Failed to load deal pipeline. Is the API reachable?</Text>;
  if (!data?.deal) return <Text p="md">Deal not found.</Text>;

  const { deal, lines = [], latestRun, runs = [], enrichment, aiTraffic = [], liveInventory = [], liveCoverage } = data;
  // Flags are PER LINE now — the deal-level flags_json is retired along with opportunity_summary. This is a
  // deal-level view, so it shows the union of its lines' flags (deduped), which is what the old deal-level
  // column was approximating anyway. A line's own flags live on its row in the cockpit.
  const flags = (() => {
    try {
      const les = JSON.parse(enrichment?.line_enrichment_json || '[]');
      return [...new Set(les.flatMap((le) => (Array.isArray(le?.flags) ? le.flags : [])))];
    } catch { return []; }
  })();
  const guidanceApplied = (() => { try { return JSON.parse(enrichment?.guidance_applied_json || 'null'); } catch { return null; } })();
  const cited = guidanceApplied?.cited || [];
  const bundle = guidanceApplied?.bundle || null;
  const escalated = enrichment?.escalated === true || enrichment?.escalated === 1;

  return (
    <Stack gap="md" p="md">
      {/* Header */}
      <Group justify="space-between" align="flex-start">
        <div>
          <Group gap="xs">
            <Anchor size="sm" onClick={() => navigate('/sales/funnel/rotabull-deals')}>← Rotabull Deals</Anchor>
          </Group>
          <Title order={2}>{deal.buyer_company_name || '(unknown customer)'}</Title>
          <Text size="sm" c="dimmed">
            {deal.deal_public_id} · {deal.source || '—'} · {deal.priority || '—'} · created {fmt(deal.rfq_create_date)}
          </Text>
        </div>
        <Stack gap="xs" align="flex-end">
          <Group gap={4}>
            <Button size="xs" loading={isEnriching} onClick={() => runEnrich(false)}>Enrich deal</Button>
            <Menu position="bottom-end" withinPortal>
              <Menu.Target><Button size="xs" variant="light" px={8} disabled={isEnriching}>▾</Button></Menu.Target>
              <Menu.Dropdown>
                <Menu.Item onClick={() => runEnrich(true)}>Enrich + adjudicate (gpt-5)</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
          {enrichMsg && <Text size="xs" c={enrichMsg.ok ? 'green' : 'red'}>{enrichMsg.text}</Text>}
          {latestRun ? (
            <Paper withBorder p="sm" radius="md">
              <Group gap="xs">
                <Text size="sm" fw={600}>Latest run</Text>
                <Badge color={stColor(latestRun.status)} variant="filled">{latestRun.status}</Badge>
              </Group>
              <Text size="xs" c="dimmed">{fmt(latestRun.started_at)} → {fmt(latestRun.completed_at)} · {latestRun.triggered_by}</Text>
            </Paper>
          ) : <Badge color="gray" variant="light" size="lg">Not yet enriched</Badge>}
        </Stack>
      </Group>

      {/* Requested parts */}
      <Paper withBorder radius="md" p="sm">
        <Text fw={600} size="sm" mb="xs">Requested parts ({lines.length})</Text>
        <Table fz="xs">
          <Table.Thead><Table.Tr>
            <Table.Th>Part</Table.Th><Table.Th>Description</Table.Th><Table.Th>Cond</Table.Th><Table.Th>Qty</Table.Th><Table.Th>Service</Table.Th>
          </Table.Tr></Table.Thead>
          <Table.Tbody>
            {lines.map((l) => (
              <Table.Tr key={l.deal_line_id}>
                <Table.Td>{l.part_number}</Table.Td>
                <Table.Td>{l.description}</Table.Td>
                <Table.Td>{l.condition_code}</Table.Td>
                <Table.Td>{l.quantity}</Table.Td>
                <Table.Td>{l.service_requested}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Paper>

      {/* Live inventory (current) — deterministic; shown regardless of enrichment status */}
      {liveCoverage && (
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Live inventory (current)</Text>
            <Badge color={COVERAGE_BADGE[liveCoverage.inventory_coverage] || 'gray'} variant="light">
              {liveCoverage.inventory_coverage} ({liveCoverage.inventory_coverage_pct ?? 0}%) · margin {liveCoverage.margin_estimate ?? '—'}
            </Badge>
          </Group>
          <Table fz="xs">
            <Table.Thead><Table.Tr>
              <Table.Th>Part</Table.Th><Table.Th>Cond</Table.Th><Table.Th>Req</Table.Th>
              <Table.Th>On hand (now)</Table.Th><Table.Th>At RFQ</Table.Th><Table.Th>Fill</Table.Th>
              <Table.Th>Sell</Table.Th><Table.Th>Margin $</Table.Th>
            </Table.Tr></Table.Thead>
            <Table.Tbody>
              {liveInventory.map((r) => {
                const cur = r.current_qty ?? 0, atr = r.at_rfq_qty ?? 0;
                const changed = cur !== atr;
                return (
                  <Table.Tr key={r.deal_line_id}>
                    <Table.Td>{r.part_number}</Table.Td>
                    <Table.Td>{r.condition_code || '—'}</Table.Td>
                    <Table.Td>{r.requested_qty}</Table.Td>
                    <Table.Td>{cur}</Table.Td>
                    <Table.Td><Text size="xs" c={changed ? 'orange' : 'dimmed'} fw={changed ? 600 : undefined}>{atr}{changed ? ' ⚠' : ''}</Text></Table.Td>
                    <Table.Td><Badge size="xs" color={r.has_match ? 'green' : 'red'} variant="light">{r.has_match ? 'yes' : 'no'}</Badge></Table.Td>
                    <Table.Td>{r.sell_price ?? '—'}</Table.Td>
                    <Table.Td>{r.margin_dollars ?? '—'}</Table.Td>
                  </Table.Tr>
                );
              })}
              {liveInventory.length === 0 && <Table.Tr><Table.Td colSpan={8}><Text c="dimmed" ta="center" py="sm">No line inventory.</Text></Table.Td></Table.Tr>}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      {/* Guidance applied (from last enrichment) — house/segment/customer the model reasoned over */}
      {bundle && (bundle.house?.length > 0 || bundle.segment || bundle.customer) && (
        <Paper withBorder radius="md" p="md">
          <Group justify="space-between" mb="xs">
            <Text fw={600}>Guidance applied</Text>
            {bundle.hard_rules?.length > 0 && (
              <Group gap={4}>{bundle.hard_rules.map((h, i) => <Badge key={i} color="red" variant="filled" size="xs">hard: {h.kind}</Badge>)}</Group>
            )}
          </Group>
          {bundle.segment && (
            <div style={{ marginBottom: 8 }}>
              <Text size="xs" c="dimmed">Segment · {bundle.segment.key}</Text>
              <Text size="sm">{bundle.segment.posture}</Text>
              {bundle.segment.pricing_stance && <Text size="xs" c="dimmed">Pricing: {bundle.segment.pricing_stance}</Text>}
            </div>
          )}
          {bundle.customer && (
            <div style={{ marginBottom: 8 }}>
              <Text size="xs" c="dimmed">Customer note · {bundle.customer.acctno}</Text>
              <Text size="sm">{bundle.customer.note}</Text>
            </div>
          )}
          {bundle.house?.length > 0 && (
            <>
              <Text size="xs" c="dimmed">House rules</Text>
              <Stack gap={2}>
                {bundle.house.map((h, i) => (
                  <Text key={i} size="xs">
                    {h.hard_rule_type ? <Badge color="red" variant="light" size="xs" mr={4}>{h.hard_rule_type}</Badge> : null}
                    <Text span c="dimmed">{h.key}:</Text> {h.text}
                  </Text>
                ))}
              </Stack>
            </>
          )}
        </Paper>
      )}

      {latestRun ? (
        <>
          {/* Stage status strip */}
          <Group gap="xs">
            {STAGES.map((s) => (
              <Badge key={s.n} color={stColor(latestRun[s.key])} variant="light">
                S{s.n} {s.name}: {latestRun[s.key] || '—'}
              </Badge>
            ))}
          </Group>

          {/* Per-stage detail */}
          <Accordion multiple variant="separated" defaultValue={['stage-3', 'stage-4']}>
            {STAGES.map((s) => {
              const status = latestRun[s.key];
              const err = latestRun[s.err];
              return (
                <Accordion.Item key={s.n} value={`stage-${s.n}`}>
                  <Accordion.Control>
                    <Group gap="sm">
                      <Badge color={stColor(status)} variant="filled" size="sm">Stage {s.n}</Badge>
                      <Text fw={600}>{s.name}</Text>
                      <Text size="xs" c="dimmed">{s.kind}</Text>
                      {s.prefix && (
                        <Text size="xs" c="dimmed">
                          {latestRun[`${s.prefix}_model`]} · {latestRun[`${s.prefix}_input_tokens`]}/{latestRun[`${s.prefix}_output_tokens`]} tok · {latestRun[`${s.prefix}_latency_ms`]}ms
                        </Text>
                      )}
                      {s.key === 'judgement_status' && escalated && (
                        <Badge size="xs" color="grape" variant="filled">escalated → gpt-5</Badge>
                      )}
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    {err && <Text c="red" size="sm" mb="xs">Error: {err}</Text>}
                    {s.n === 1 && (<>
                      <Text size="xs" c="dimmed" mb={4}>context_hash: {latestRun.context_hash}</Text>
                      <JsonBlock value={latestRun.context_json} />
                    </>)}
                    {s.key === 'score_status' && <JsonBlock value={enrichment?.numeric_scores_json} />}
                    {s.key === 'pricing_status' && <JsonBlock value={enrichment?.draft_quote_json} />}
                    {s.key === 'judgement_status' && (<>
                      <Text size="xs" c="dimmed" mb={4}>Per-line judgement (one entry per deal line):</Text>
                      <JsonBlock value={enrichment?.line_enrichment_json} />
                    </>)}
                    {s.outCol && (
                      <Tabs defaultValue="output" mt="xs">
                        <Tabs.List>
                          <Tabs.Tab value="output">Output{s.key === 'judgement_status' ? ' (driving line)' : ''}</Tabs.Tab>
                          <Tabs.Tab value="input">Input (prompt)</Tabs.Tab>
                        </Tabs.List>
                        <Tabs.Panel value="output" pt="xs"><JsonBlock value={latestRun[s.outCol]} /></Tabs.Panel>
                        <Tabs.Panel value="input" pt="xs"><JsonBlock value={latestRun[s.inCol]} maxHeight={500} /></Tabs.Panel>
                      </Tabs>
                    )}
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>

          {/* Final enrichment output */}
          {enrichment && (
            <Paper withBorder radius="md" p="md">
              <Group justify="space-between" mb="xs">
                <Text fw={600}>Enrichment output</Text>
                <Group gap={6}>
                  {enrichment.judgement_model && (
                    <Badge color={escalated ? 'grape' : 'blue'} variant={escalated ? 'filled' : 'light'}>
                      scored by {enrichment.judgement_model}{escalated ? ' (escalated)' : ''}
                    </Badge>
                  )}
                  {escalated && enrichment.escalation_reason && (
                    <Badge color="grape" variant="light">{enrichment.escalation_reason}</Badge>
                  )}
                  {enrichment.adjudication_needed ? <Badge color="orange" variant="light">adjudication needed</Badge> : null}
                </Group>
              </Group>
              <SimpleGrid cols={{ base: 2, sm: 4 }} mb="sm">
                <div><Text size="xs" c="dimmed">Priority score</Text><Title order={3}>{enrichment.priority_score ?? '—'}</Title></div>
                <div><Text size="xs" c="dimmed">Recommended action</Text><Badge variant="dot">{enrichment.recommended_action || '—'}</Badge></div>
                <div><Text size="xs" c="dimmed">Treatment stance</Text><Badge variant="light" color={STANCE_COLOR[enrichment.treatment_stance] || 'gray'}>{enrichment.treatment_stance || '—'}</Badge></div>
                <div><Text size="xs" c="dimmed">Coverage (at last enrichment)</Text><Text>{enrichment.inventory_coverage || '—'} ({enrichment.inventory_coverage_pct ?? 0}%)</Text></div>
              </SimpleGrid>
              {/* The customer brief only. `opportunity_summary` is gone: it was DEAL-grain prose naming the
                  deal's other parts, and it has been replaced by a per-LINE summary (LineEnrichment.line_summary),
                  which belongs against a line — not in a deal-level panel. */}
              {enrichment.customer_summary && <><Text size="xs" c="dimmed">Customer</Text><Text size="sm" mb="xs">{enrichment.customer_summary}</Text></>}
              {enrichment.priority_rationale && <><Text size="xs" c="dimmed">Priority rationale</Text><Text size="sm" mb="xs">{enrichment.priority_rationale}</Text></>}
              {cited.length > 0 && (
                <><Text size="xs" c="dimmed" mt="xs">Guidance applied</Text>
                <Group gap={4}>{cited.map((g, i) => <Badge key={i} color="teal" variant="light" size="xs">{g}</Badge>)}</Group></>
              )}
              {flags.length > 0 && <Group gap={4} mt="xs">{flags.map((f, i) => <Badge key={i} color="yellow" variant="light" size="xs">{typeof f === 'string' ? f : (f.message || f.type)}</Badge>)}</Group>}
            </Paper>
          )}

          {/* AI traffic (unified log) */}
          {aiTraffic.length > 0 && (
            <Paper withBorder radius="md" p="md">
              <Text fw={600} mb="xs">AI traffic ({aiTraffic.length})</Text>
              <Table fz="xs">
                <Table.Thead><Table.Tr>
                  <Table.Th>Time</Table.Th><Table.Th>Source</Table.Th><Table.Th>Model</Table.Th><Table.Th>Prompt</Table.Th><Table.Th>Tokens</Table.Th><Table.Th>ms</Table.Th><Table.Th>Finish</Table.Th>
                </Table.Tr></Table.Thead>
                <Table.Tbody>
                  {aiTraffic.map((t) => (
                    <Table.Tr key={t.Id}>
                      <Table.Td>{fmt(t.RequestTime)}</Table.Td><Table.Td>{t.Source}</Table.Td><Table.Td>{t.ModelName}</Table.Td>
                      <Table.Td>{t.PromptVersion}</Table.Td><Table.Td>{t.InputTokens}/{t.OutputTokens}</Table.Td>
                      <Table.Td>{t.LatencyMs}</Table.Td><Table.Td>{t.FinishReason}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}

          {/* Run history */}
          {runs.length > 1 && (
            <Paper withBorder radius="md" p="md">
              <Text fw={600} mb="xs">Run history ({runs.length})</Text>
              <Table fz="xs">
                <Table.Thead><Table.Tr>
                  <Table.Th>Started</Table.Th><Table.Th>Status</Table.Th><Table.Th>Trigger</Table.Th>
                  <Table.Th>S1</Table.Th><Table.Th>S2</Table.Th><Table.Th>S3</Table.Th><Table.Th>S4</Table.Th><Table.Th>S5</Table.Th>
                </Table.Tr></Table.Thead>
                <Table.Tbody>
                  {runs.map((r) => (
                    <Table.Tr key={r.id}>
                      <Table.Td>{fmt(r.started_at)}</Table.Td>
                      <Table.Td><Badge size="xs" color={stColor(r.status)} variant="light">{r.status}</Badge></Table.Td>
                      <Table.Td>{r.triggered_by}</Table.Td>
                      {STAGES.map((s) => <Table.Td key={s.key}><Badge size="xs" color={stColor(r[s.key])} variant="dot">{r[s.key]?.[0] ?? '—'}</Badge></Table.Td>)}
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </>
      ) : (
        <Paper withBorder radius="md" p="md">
          <Text c="dimmed">This deal has not been enriched yet — it's in the pending backlog. Enable the enrichment worker (or trigger a manual enrich) and reload to see the pipeline.</Text>
        </Paper>
      )}
    </Stack>
  );
}
